/**
 * Orçamentos Page
 *
 * Main page for viewing and managing budget phases for a selected project
 *
 * Layout:
 * - Header with project selector and action buttons
 * - Two-column layout:
 *   - Left column (812px): Stack of budget phase cards
 *   - Right column (716px): Horizontal phase comparison chart
 * - Gap between columns: 32px
 *
 * Features:
 * - Project selection dropdown
 * - Collapsible phase cards with line items
 * - Progress tracking for each phase
 * - Action buttons (view, edit, delete) with proper handlers
 * - Phase comparison chart
 * - Delete confirmation dialog
 * - Error handling and loading states
 */

import { useState, useEffect, useMemo } from "react";
import { Plus, Sheet } from "lucide-react";
import { useLocation } from "wouter";
import { AppContainer } from "@/components/layout/app-container";
import { Button } from "@/components/ui/button";
import { ProjectSelector } from "@/components/orcamentos/project-selector";
import { BudgetPhaseCard } from "@/components/orcamentos/budget-phase-card";
import { HorizontalPhaseChart } from "@/components/orcamentos/horizontal-phase-chart";
import { PhaseActionModal } from "@/components/orcamentos/phase-action-modal";
import { DeletePhaseDialog } from "@/components/orcamentos/delete-phase-dialog";
import { toast } from "@/hooks/use-toast";
import { useGetProjects } from "@/services/queries/useProject";
import { useGetBudgetByMovie, useGetBudgetTable } from "@/services/queries/useBudget";
import { transformBudgetTableToPhases, transformPhasesToChartData } from "@/services/orcamentos/transform-budget-data";
import { BudgetPhase, BudgetTableStepResponse } from "@/types/orcamentos.interface";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { budgetService } from "@/services/end-points/orcamento/budget-api";

export default function Orcamentos() {
  const [, setLocation] = useLocation();

  // Fetch projects
  // Note: React Query + Axios api wrapper returns data directly (array of IProject)
  const { data: projectsData, isLoading: projectsLoading } = useGetProjects();
  const projects = useMemo(() => {
    if (!projectsData || !Array.isArray(projectsData)) return [];
    return projectsData;
  }, [projectsData]);

  // State for selected project
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  // Auto-select first project when loaded
  useEffect(() => {
    if (projects && projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  // Fetch budget summary first to get the budgetId for the current project
  const {
    data: budgetData,
    isLoading: budgetLoading,
  } = useGetBudgetByMovie(selectedProjectId || 0, !!selectedProjectId);

  const budgetId = budgetData?.data?.id;

  // Then fetch the table specifically using budgetId to get payment data
  const {
    data: tableDataResponse,
    isLoading: tableLoading,
  } = useGetBudgetTable(budgetId || 0, !!budgetId);

  const isLoading = budgetLoading || tableLoading;

  // Transform budget data to hierarchical structure for display
  const budgetPhases: BudgetPhase[] = useMemo(() => {
    if (!tableDataResponse) return [];
    return transformBudgetTableToPhases(tableDataResponse as BudgetTableStepResponse[]);
  }, [tableDataResponse]);

  // Transform phases to chart data
  const chartData = useMemo(() => {
    return transformPhasesToChartData(budgetPhases);
  }, [budgetPhases]);

  const queryClient = useQueryClient();

  // Delete line item mutation
  const deleteMutation = useMutation({
    mutationFn: ({ budgetId, itemId }: { budgetId: number; itemId: number }) =>
      budgetService.deleteLineItem(budgetId, itemId),
  });

  // Format projects for selector dropdown
  const availableProjects = projects?.map((p) => ({
    id: String(p.id),
    name: p.name,
  })) || [];

  const currentSelectedProject = availableProjects.find(
    (p) => p.id === String(selectedProjectId)
  ) || availableProjects[0];

  // State for delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    phaseId: "",
    phaseName: "",
    isDeleting: false,
  });

  // State for phase action modal
  const [phaseModal, setPhaseModal] = useState<{
    open: boolean;
    phase: BudgetPhase | null;
  }>({
    open: false,
    phase: null,
  });

  // State for loading operations
  const [loadingPhaseId, setLoadingPhaseId] = useState<string | null>(null);

  /**
   * Handle project change
   */
  const handleProjectChange = (projectId: string) => {
    try {
      setSelectedProjectId(parseInt(projectId));
      const project = availableProjects.find((p) => p.id === projectId);
      if (project) {
        toast({
          title: "Projeto selecionado",
          description: `Carregando dados de ${project.name}`,
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar projeto",
        description: "Não foi possível carregar os dados do projeto",
        variant: "destructive",
      });
      console.error("Error changing project:", error);
    }
  };

  /**
   * Handle phase view
   * Navigate to budget table view filtered by phase
   */
  const handlePhaseView = (phaseId: string) => {
    try {
      const phase = budgetPhases.find((p) => p.id === phaseId);
      if (!phase) {
        throw new Error("Fase não encontrada");
      }

      setPhaseModal({
        open: true,
        phase,
      });
    } catch (error) {
      toast({
        title: "Erro ao visualizar fase",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      console.error("Error viewing phase:", error);
    }
  };

  /**
   * Open delete confirmation dialog
   */
  const openDeleteDialog = (phaseId: string) => {
    try {
      const phase = budgetPhases.find((p) => p.id === phaseId);
      if (!phase) {
        throw new Error("Fase não encontrada");
      }

      setDeleteDialog({
        open: true,
        phaseId,
        phaseName: phase.phaseName,
        isDeleting: false,
      });
    } catch (error) {
      toast({
        title: "Erro ao deletar fase",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  /**
   * Close delete confirmation dialog
   */
  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      phaseId: "",
      phaseName: "",
      isDeleting: false,
    });
  };

  /**
   * Confirm phase deletion
   * Note: Phases are defined by ANCINE and cannot be deleted from the system.
   * This would only delete the items within the phase.
   */
  const handleConfirmDelete = async () => {
    if (!budgetId) return;

    try {
      setDeleteDialog((prev) => ({ ...prev, isDeleting: true }));

      const phase = budgetPhases.find((p) => p.id === deleteDialog.phaseId);
      if (!phase) throw new Error("Fase não encontrada");

      // Check if any subitem in this phase has value > 0 (meaning there are payments)
      const hasCompletedPayments = phase.topics.some((topic) =>
        topic.lineItems.some((item) => item.value > 0)
      );

      if (hasCompletedPayments) {
        toast({
          title: "Não é possível excluir",
          description: "Subitens com pagamentos realizados não podem ser apagados, apenas movidos para outra fase.",
          variant: "destructive",
        });
        closeDeleteDialog();
        return;
      }

      // If no payments, iterate and delete all subitems inside this phase
      const itemsToDelete = phase.topics.flatMap((topic) =>
        topic.lineItems.map((item) => parseInt(item.id))
      );

      // We fire multiple requests sequentially to avoid overwhelming the server, 
      // but in a real case a bulk delete endpoint would be better.
      for (const itemId of itemsToDelete) {
        await deleteMutation.mutateAsync({ budgetId, itemId });
      }

      toast({
        title: "Fase zerada",
        description: "Todos os subitens foram excluídos.",
      });

      // Refetch table
      queryClient.invalidateQueries({ queryKey: ['budget-table', budgetId] });

      closeDeleteDialog();
    } catch (error) {
      toast({
        title: "Erro ao limpar fase",
        description:
          error instanceof Error ? error.message : "Erro ao limpar fase",
        variant: "destructive",
      });
      console.error("Error deleting phase items:", error);
    } finally {
      setDeleteDialog((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  /**
   * Handle view table button
   * Navigate to budget table view
   */
  const handleViewTable = () => {
    setLocation("/financeiro/orcamentos/tabela");
  };

  /**
   * Handle create new project button
   */
  const handleCreateNew = () => {
    try {
      setLocation("/cadastrar-projeto");
    } catch (error) {
      toast({
        title: "Erro ao navegar",
        description: "Não foi possível abrir o formulário de criação",
        variant: "destructive",
      });
      console.error("Navigation error:", error);
    }
  };

  // Loading state
  if (projectsLoading) {
    return (
      <AppContainer title="Orçamentos" description="Carregando projetos...">
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-gray-400">Carregando projetos...</p>
        </div>
      </AppContainer>
    );
  }

  // No projects state
  if (!projects || projects.length === 0) {
    return (
      <AppContainer
        title="Orçamentos"
        description="Nenhum projeto encontrado"
        button={
          <Button
            size="sm"
            className="bg-[#7846FF] hover:bg-[#9b6fff] text-[#F5F2FF]"
            onClick={handleCreateNew}
          >
            <Plus size={16} />
            Criar Novo
          </Button>
        }
      >
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="text-gray-400 text-sm">Nenhum projeto encontrado. Crie um projeto primeiro.</p>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer
      title="Orçamentos"
      description="Texto de Apoio da pagina"
      button={
        <div className="flex items-center gap-4">
          <ProjectSelector
            projects={availableProjects}
            selectedProject={currentSelectedProject}
            onProjectChange={handleProjectChange}
          />
          <Button
            onClick={handleViewTable}
            variant="outline"
            size="sm"
            className="border-[#7846FF] text-[#7846FF] hover:bg-[#7846FF] hover:text-white"
          >
            <Sheet size={16} className="mr-1" />
            Tabela Orçamentária
          </Button>
        </div>
      }
    >
      {/* Loading budget */}
      {budgetLoading && (
        <div className="flex items-center justify-center min-h-[150px]">
          <p className="text-gray-400 text-sm">Carregando orçamento...</p>
        </div>
      )}

      {/* No budget found */}
      {!isLoading && !tableDataResponse && !!selectedProjectId && (
        <div className="flex items-center justify-center min-h-[150px]">
          <p className="text-gray-400 text-sm">
            Nenhum orçamento encontrado para este projeto. Crie um orçamento primeiro.
          </p>
        </div>
      )}

      {/* Main Content - 2 Column Layout */}
      {!isLoading && !!tableDataResponse && (
        <div className="flex gap-4">
          {/* Left Column - Budget Phase Cards */}
          <div className="flex flex-col gap-4 flex-1 min-w-0">
            {budgetPhases.length === 0 ? (
              <div className="flex items-center justify-center min-h-[150px]">
                <p className="text-gray-400 text-sm">
                  Nenhuma fase de orçamento encontrada. Adicione itens ao orçamento.
                </p>
              </div>
            ) : (
              budgetPhases.map((phase) => (
                <BudgetPhaseCard
                  key={phase.id}
                  phase={phase}
                  isLoading={loadingPhaseId === phase.id || deleteDialog.isDeleting}
                  onView={() => handlePhaseView(phase.id)}
                  onDelete={() => openDeleteDialog(phase.id)}
                />
              ))
            )}
          </div>

          {/* Right Column - Budget Summary + Horizontal Phase Chart */}
          <div className="w-[400px] xl:w-[500px] shrink-0 flex flex-col gap-4">
            {/* Budget summary card */}
            {budgetData?.data && (
              <div className="bg-darkblue rounded-lg px-5 py-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1 bg-white/5 rounded-lg px-4 py-3 border border-white/10">
                    <span className="text-xs text-gray-400 font-medium">Orçamento Planejado</span>
                    <span className="text-xl font-bold text-white tracking-tight">
                      {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
                        Number(budgetData.data.amount) || 0
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 bg-white/5 rounded-lg px-4 py-3 border border-white/10">
                    <span className="text-xs text-gray-400 font-medium">Orçamento Captado</span>
                    <span className="text-xl font-bold text-emerald-400 tracking-tight">
                      {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
                        Number(budgetData.data.total_amount) || 0
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <HorizontalPhaseChart data={chartData} />
          </div>
        </div>
      )}

      <DeletePhaseDialog
        open={deleteDialog.open}
        phaseName={deleteDialog.phaseName}
        isDeleting={deleteDialog.isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteDialog}
      />

      {/* Phase Action Modal */}
      <PhaseActionModal
        open={phaseModal.open}
        onOpenChange={(open) => setPhaseModal((prev) => ({ ...prev, open }))}
        phase={phaseModal.phase}
        onEditSubitems={() => {
          setPhaseModal((prev) => ({ ...prev, open: false }));
          setLocation("/financeiro/orcamentos/tabela");
        }}
      />
    </AppContainer>
  );
}
